import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';

const API_BASE = 'https://d3bdqxkpn753kf.cloudfront.net';

const STEPS = ['Gothram & Surname', 'Email & Phone'];

const Field = ({ label, children }) => (
  <div>
    <label style={{ display: 'block', fontFamily: 'Poppins, sans-serif', fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#6B7280', marginBottom: '0.2rem' }}>{label}</label>
    {children}
  </div>
);

const SignupPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(0);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');

  // Step 1
  const [gothram, setGothram] = useState('');
  const [surname, setSurname] = useState('');
  const [gothramOptions, setGothramOptions] = useState([]);
  const [surnameOptions, setSurnameOptions] = useState([]);
  const [showGothramDrop, setShowGothramDrop] = useState(false);
  const [showSurnameDrop, setShowSurnameDrop] = useState(false);
  const [gothramValid, setGothramValid] = useState(false);
  const [surnameValid, setSurnameValid] = useState(false);
  const gothramRef = useRef(null);
  const surnameRef = useRef(null);

  // Step 2
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  useEffect(() => {
    setStep(0); setError(null);
    setGothram(''); setSurname(''); setGothramValid(false); setSurnameValid(false);
    setEmail(''); setPhone('');
  }, [location.key]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (gothramRef.current && !gothramRef.current.contains(e.target)) setShowGothramDrop(false);
      if (surnameRef.current && !surnameRef.current.contains(e.target)) setShowSurnameDrop(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Gothram typeahead
  const gothramTimer = useRef(null);
  const handleGothramChange = (val) => {
    setGothram(val);
    setGothramValid(false);
    if (val.trim().length < 3) { setGothramOptions([]); setShowGothramDrop(false); return; }
    clearTimeout(gothramTimer.current);
    gothramTimer.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE}/vrv-gothram-surname-validate`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'search_gothram', query: val.trim() })
        });
        const data = await res.json();
        if (res.ok) {
          const opts = data.gothrams || [];
          setGothramOptions(opts);
          setShowGothramDrop(opts.length > 0);
        }
      } catch { /* silent */ }
    }, 300);
  };

  const selectGothram = (g) => {
    setGothram(g); setGothramValid(true); setShowGothramDrop(false);
    setSurname(''); setSurnameValid(false); setSurnameOptions([]);
  };

  // Surname typeahead
  const surnameTimer = useRef(null);
  const handleSurnameChange = (val) => {
    setSurname(val);
    setSurnameValid(false);
    if (!gothramValid || val.trim().length < 3) { setSurnameOptions([]); setShowSurnameDrop(false); return; }
    clearTimeout(surnameTimer.current);
    surnameTimer.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE}/vrv-gothram-surname-validate`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'search_surname', gothram, query: val.trim() })
        });
        const data = await res.json();
        if (res.ok) {
          const opts = data.surnames || [];
          setSurnameOptions(opts);
          setShowSurnameDrop(opts.length > 0);
        }
      } catch { /* silent */ }
    }, 300);
  };

  const selectSurname = (s) => {
    setSurname(s); setSurnameValid(true); setShowSurnameDrop(false);
  };

  // Step 1: Validate gothram+surname combination
  const handleStep1 = async () => {
    setError(null); setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/vrv-gothram-surname-validate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'validate', gothram, surname })
      });
      const data = await res.json();
      if (res.ok && data.valid) setStep(1);
      else setError(data.message || 'Invalid combination');
    } catch { setError('Error connecting to server'); }
    setLoading(false);
  };

  // Step 2: Validate email/phone against VRV_USERS
  const handleStep2 = async () => {
    setError(null); setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/vrv-signup-initiate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ gothram, surname, emailID: email, phoneNumber: phone })
      });
      const data = await res.json();
      if (res.ok) {
        // Always show same popup message regardless of user existence
        setPopupMessage(data.message || 'If you are eligible for signup, you will receive a registration link on your email shortly.');
        setShowPopup(true);
        setTimeout(() => { setShowPopup(false); navigate('/'); }, 4000);
      } else { setError(data.message || 'Something went wrong'); }
    } catch { setError('Error connecting to server'); }
    setLoading(false);
  };

  const inputStyle = { minHeight: '38px', height: '38px', padding: '0 0.75rem', fontSize: '0.82rem' };

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <div className="space-y-3">
            <Field label="Gothram *">
              <div ref={gothramRef} className="relative">
                <input type="text" placeholder="Type at least 3 characters..." className="vrv-input" style={inputStyle}
                  value={gothram} onChange={e => handleGothramChange(e.target.value)} autoComplete="off" />
                {showGothramDrop && gothramOptions.length > 0 && (
                  <div className="absolute z-20 left-0 right-0 mt-1 rounded-xl overflow-hidden shadow-lg" style={{ background: 'white', border: '1px solid rgba(0,0,0,0.08)', maxHeight: '160px', overflowY: 'auto' }}>
                    {gothramOptions.map(g => (
                      <button key={g} onClick={() => selectGothram(g)} className="w-full text-left px-4 py-2.5 text-sm hover:bg-amber-50 transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>{g}</button>
                    ))}
                  </div>
                )}
                {/* no tick shown */}
              </div>
            </Field>
            <Field label="Surname *">
              <div ref={surnameRef} className="relative">
                <input type="text" placeholder={gothramValid ? "Type at least 3 characters..." : "Select gothram first"} className="vrv-input" style={inputStyle}
                  value={surname} onChange={e => handleSurnameChange(e.target.value)} disabled={!gothramValid} autoComplete="off" />
                {showSurnameDrop && surnameOptions.length > 0 && (
                  <div className="absolute z-20 left-0 right-0 mt-1 rounded-xl overflow-hidden shadow-lg" style={{ background: 'white', border: '1px solid rgba(0,0,0,0.08)', maxHeight: '160px', overflowY: 'auto' }}>
                    {surnameOptions.map(s => (
                      <button key={s} onClick={() => selectSurname(s)} className="w-full text-left px-4 py-2.5 text-sm hover:bg-amber-50 transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>{s}</button>
                    ))}
                  </div>
                )}
                {/* no tick shown */}
              </div>
            </Field>
            <button onClick={handleStep1} disabled={loading || !gothramValid || !surnameValid} className="btn-gold w-full" style={{ height: '40px', fontSize: '0.82rem' }}>
              {loading ? 'Please wait...' : 'Next →'}
            </button>
          </div>
        );
      case 1:
        return (
          <div className="space-y-3">
            <Field label="Email Address *">
              <input type="email" placeholder="you@example.com" className="vrv-input" style={inputStyle} value={email} onChange={e => setEmail(e.target.value)} required />
            </Field>
            <Field label="Phone Number *">
              <input type="tel" placeholder="+91 00000 00000" className="vrv-input" style={inputStyle} value={phone} onChange={e => setPhone(e.target.value)} required />
            </Field>
            <button onClick={handleStep2} disabled={loading || !email || !phone} className="btn-gold w-full" style={{ height: '40px', fontSize: '0.82rem' }}>
              {loading ? 'Please wait...' : 'Verify & Continue →'}
            </button>
            <button onClick={() => { setStep(0); setError(null); }} className="w-full text-center text-xs font-bold uppercase tracking-wider mt-1" style={{ color: '#6B7280', fontFamily: 'Poppins, sans-serif' }}>
              ← Back
            </button>
          </div>
        );
      default: return null;
    }
  };

  return (
    <div className="h-screen flex overflow-hidden" style={{ background: '#F8F5EE' }}>
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-5/12 flex-col justify-between p-14 relative overflow-hidden"
        style={{ background: 'linear-gradient(160deg, #0F172A 0%, #16213E 100%)', borderRight: '1px solid rgba(212,175,55,0.12)' }}>
        <div className="absolute inset-0 opacity-[0.025] pointer-events-none flex items-center justify-center select-none" style={{ fontSize: '50vw', fontFamily: '"Cormorant Garamond", serif', color: 'white', overflow: 'hidden' }}>ॐ</div>
        <div className="relative z-10">
          <p className="vrv-tag mb-5">Join the Community</p>
          <h2 className="text-5xl font-bold leading-tight mb-6 text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Be part of<br />
            <span style={{ background: 'linear-gradient(135deg, #D4AF37, #B9932F)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>something</span><br />
            sacred
          </h2>
          <div className="vrv-divider mb-6" style={{ width: '60px' }} />
          <div className="space-y-3">
            {['Connect with Vaikhanasas worldwide', 'Access spiritual content & events', 'Support temple traditions'].map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: '#D4AF37' }} />
                <p className="text-sm" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'Inter, sans-serif' }}>{item}</p>
              </div>
            ))}
          </div>
        </div>
        <p className="text-xs italic relative z-10" style={{ color: 'rgba(212,175,55,0.3)', fontFamily: '"Cormorant Garamond", serif' }}>
          "Preserving the sacred tradition for generations"
        </p>
      </div>

      {/* Right Panel */}
      <div className="flex-1 h-full flex flex-col justify-center px-6 sm:px-12 lg:px-14 overflow-hidden">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-md mx-auto">
          <p className="vrv-tag mb-0.5" style={{ color: '#D4AF37' }}>Get Started</p>
          <h1 className="text-xl font-bold mb-1" style={{ fontFamily: 'Poppins, sans-serif', color: '#111827' }}>Create Account</h1>
          <p className="mb-4" style={{ color: '#6B7280', fontFamily: 'Inter, sans-serif', fontSize: '0.72rem' }}>Register to join the Vaikhanasa family</p>

          {/* Stepper */}
          <div className="flex items-center gap-1 mb-5">
            {STEPS.map((s, i) => (
              <div key={i} className="flex items-center gap-1 flex-1">
                <div className="flex items-center justify-center w-5 h-5 rounded-full text-[9px] font-bold transition-all"
                  style={{
                    background: i <= step ? 'linear-gradient(135deg, #D4AF37, #B9932F)' : '#E5E7EB',
                    color: i <= step ? '#0F172A' : '#9CA3AF',
                  }}>
                  {i < step ? '✓' : i + 1}
                </div>
                {i < STEPS.length - 1 && (
                  <div className="flex-1 h-0.5 rounded-full" style={{ background: i < step ? '#D4AF37' : '#E5E7EB' }} />
                )}
              </div>
            ))}
          </div>
          <p className="text-[10px] font-bold uppercase tracking-wider mb-3" style={{ color: '#D4AF37', fontFamily: 'Poppins, sans-serif' }}>
            {STEPS[step]}
          </p>

          {renderStep()}

          {error && (
            <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
              className="mt-3 px-3 py-2 rounded-xl flex items-start gap-2"
              style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', fontFamily: 'Inter, sans-serif', fontSize: '0.75rem' }}>
              <span>⚠️</span><span>{error}</span>
            </motion.div>
          )}

          <p className="mt-4 text-center" style={{ color: '#6B7280', fontFamily: 'Inter, sans-serif', fontSize: '0.75rem' }}>
            Already have an account?{' '}
            <Link to="/login" className="font-bold transition-colors" style={{ color: '#16213E' }}
              onMouseEnter={e => e.currentTarget.style.color = '#D4AF37'}
              onMouseLeave={e => e.currentTarget.style.color = '#16213E'}>
              Sign In →
            </Link>
          </p>
        </motion.div>
      </div>

      {/* User Exists Popup */}
      {showPopup && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(4px)' }}>
          <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.3 }}
            className="vrv-card p-10 text-center max-w-sm mx-4">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5"
              style={{ background: 'linear-gradient(135deg, #D4AF37, #B9932F)' }}>
              <span className="text-2xl">ℹ️</span>
            </div>
            <p className="text-sm leading-relaxed" style={{ color: '#374151', fontFamily: 'Inter, sans-serif' }}>{popupMessage}</p>
            <p className="text-xs mt-3" style={{ color: '#9CA3AF' }}>Redirecting to home...</p>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default SignupPage;
